import React, { useEffect, useState } from 'react';
import * as Sentry from '@sentry/browser';
import Api from '../../Api';
import { withAuth } from '../../hoc/Auth';
import { useLocation } from 'react-router-dom';
import { Wrapper, Container, Column } from '../../hoc/Layout';
import { FormattedMessage } from 'react-intl';
import ResponsiveSidebarFilters from '../../Components/Utils/ResponsiveSidebarFilters';
import AdminFiltersToggle from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle';
import AccountsList from '../../Components/ComponentsOfAdministrator/AccountsList';
import AccountsFilters from '../../Components/ComponentsOfAdministrator/AccountsFilters';
import Icon from '../../Components/Utils/Icon';
import Modal from '../../Components/Utils/Modal';
import DateRange from '../../Components/Utils/DateRange';
import Pagination from 'react-js-pagination';
import AccountEditing from '../../Components/ComponentsOfAdministrator/AccountEditing';
import AccountAdd from '../../Components/ComponentsOfAdministrator/AccountAdd';
import Search from '../../Components/Utils/SearchTags';
import EmptyCard from '../../hoc/EmptyCard';

import classes from './styles.module.css';
import filtersClasses from '../../Components/ComponentsOfAdministrator/AdminFiltersToggle/styles.module.css';

import Context from '../../Context';

const Accounts = withAuth(({ token }) => {
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [isFiltersOpen, setIsFiltersOpen] = useState(false);

    const [currentAccountsPage, setCurrentAccountsPage] = useState(1);
    const [postsPerAccountsPage, setPostsPerAccountsPage] = useState(10);
    const [accountsListCount, setAccountsListCount] = useState(10);

    const [currentAccountId, setCurrentAccountId] = useState(null);

    const [accountsSort] = useState('DESC');
    const [filterDate, setFilterDate] = useState([]);
    const [filters, setFilters] = useState([]);
    const [filtersTags, setFiltersTags] = useState([]);

    const [accountsList, setAccountsList] = useState([]);
    const [accountInfo, setAccountInfo] = useState([]);
    const [loadingAccountInfo, setLoadingAccountInfo] = useState(false);

    const [loadingAccountsList, setLoadingAccountsList] = useState(true);

    const [projects, setProjects] = useState(null);

    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tid = params.get('account');
        if (!tid) return;
        const id = parseInt(tid, 10);
        if (!id) return;
        setCurrentAccountId(id);
        setLoadingAccountInfo(true);
        new Api({ token })
            .getAccountData(id)
            .then((r) => setAccountInfo(r))
            .finally(() => setLoadingAccountInfo(false));
    }, [location.search, token]);

    useEffect(() => {
        new Api({ token }).getProjectsList({ page: 1, itemsPerPage: 10000, order: 'DESC' }).then((r) => {
            setProjects(r.data);
        });

        new Api({ token })
            .getAccountsList({ itemsPerPage: postsPerAccountsPage, page: currentAccountsPage, order: accountsSort })
            .then((r) => {
                setAccountsList(r.data);
                setAccountsListCount(r.count);
                setLoadingAccountsList(false);
            });
    }, []);

    function setPage(page, itemsPerPage) {
        if (page) setCurrentAccountsPage(page);
        else setCurrentAccountsPage(1);

        if (itemsPerPage) setPostsPerAccountsPage(itemsPerPage);

        const accountsFilters = {
            dateRange: filterDate,
            filters: filters,
            order: accountsSort,
            page: page,
            itemsPerPage: itemsPerPage,
        };

        new Api({ token }).getAccountsList(accountsFilters).then((r) => {
            setAccountsList(r.data);
        });
    }

    function onSelectAccountId(id) {
        setCurrentAccountId(id);
        setLoadingAccountInfo(true);

        new Api({ token })
            .getAccountData(id)
            .then((r) => {
                setAccountInfo(r);
            })
            .finally(() => {
                setLoadingAccountInfo(false);
            });
    }

    function onToggleModal() {
        setIsOpenModal(!isOpenModal);
    }

    function selectFilter(filter) {
        const key = Object.keys(filter)[0];
        const value = Object.values(filter)[0];

        if (value) {
            setFilters([...filters, filter]);
        } else {
            filters.map((el, index) => {
                if (Object.keys(el)[0] === key) {
                    filters.splice(index, 1);
                    setFilters(filters);
                }
                return true;
            });
        }
    }

    function applyFilters() {
        setLoadingAccountsList(true);
        setCurrentAccountsPage(1);
        setIsFiltersOpen(false);

        const searchFilters = {};
        filtersTags.forEach((tag) => {
            const key = Object.keys(tag)[0];
            const value = Object.values(tag)[0];
            searchFilters[key] = value;
        });

        if (Object.keys(searchFilters).length > 0) {
            new Api({ token })
                .makeAccountSearch(searchFilters)
                .then((r) => {
                    setAccountsList(r);
                    setAccountsListCount(r.length);
                    setLoadingAccountsList(false);
                })
                .catch((err) => {
                    Sentry.captureException(err);
                    setAccountsList([]);
                    setAccountsListCount(0);
                    setLoadingAccountsList(false);
                });
        } else {
            const accountsFilters = {
                dateRange: filterDate,
                filters: filters,
                order: accountsSort,
                page: 1,
                itemsPerPage: postsPerAccountsPage,
            };

            new Api({ token }).getAccountsList(accountsFilters).then((r) => {
                setAccountsList(r.data);
                setAccountsListCount(r.count);
                setLoadingAccountsList(false);
            });
        }
    }

    function onAccountAdd() {
        setCurrentAccountsPage(1);

        new Api({ token })
            .getAccountsList({ itemsPerPage: postsPerAccountsPage, page: currentAccountsPage, order: accountsSort })
            .then((r) => {
                setAccountsList(r.data);
                setAccountsListCount(r.count);
            });
    }

    function onChangeAccountData(data, id) {
        setAccountsList(
            accountsList.map((el) => {
                if (el.id === id) {
                    el.firstName = data.firstName;
                    el.lastName = data.lastName;
                    el.patronymic = data.patronymic;
                    el.status = data.status;
                }
                return el;
            })
        );
    }

    function onModalClose() {
        setIsOpenModal(false);
    }

    function onChangeSearch(tags) {
        setFiltersTags(tags);
    }

    return (
        <Wrapper>
            <Modal
                title={<FormattedMessage id="Accounts.modal.addTitle" defaultMessage="Добавить учетную запись" />}
                isOpen={isOpenModal}
                onClose={onModalClose}
            >
                <AccountAdd token={token} onAccountAdd={onAccountAdd} />
            </Modal>

            <Context.Provider value={{ onSelectAccountId }}>
                <Container>
                    <Column width="780" className={filtersClasses.MainColumn}>
                        <AdminFiltersToggle onClick={() => setIsFiltersOpen(true)} />
                        <Search
                            onChangeSearch={onChangeSearch}
                            onSearch={applyFilters}
                            isDisabled={false}
                            defaultSearch={{
                                key: 'accountId',
                                locale: 'Search.label.accountId',
                                placeholder: 'Search.placeholder.accountId',
                            }}
                            searchBy={[
                                {
                                    key: 'accountId',
                                    locale: 'Search.label.accountId',
                                    placeholder: 'Search.placeholder.accountId',
                                },
                                { key: 'email', locale: 'Search.label.email', placeholder: 'Search.placeholder.email' },
                                { key: 'fio', locale: 'Search.label.fio', placeholder: 'Search.placeholder.fio' },
                                {
                                    key: 'username',
                                    locale: 'Search.label.username',
                                    placeholder: 'Search.placeholder.username',
                                },
                            ]}
                        />

                        <div className={classes.Card}>
                            <div className={classes.Header}>
                                <h5 className={classes.Title}>
                                    <FormattedMessage
                                        id="Accounts.title.accountsList"
                                        defaultMessage="Учетные записи"
                                    />
                                </h5>

                                <div className={classes.Btn} onClick={onToggleModal}>
                                    <Icon className={classes.IconLeft} icon="user-plus" size={'16px'} />
                                    <FormattedMessage id="Accounts.button.add" defaultMessage="Добавить" />
                                </div>
                            </div>

                            <AccountsList
                                isLoading={loadingAccountsList}
                                data={accountsList}
                                currentId={currentAccountId}
                            />

                            {postsPerAccountsPage < accountsListCount && (
                                <div className={classes.Pagination}>
                                    <Pagination
                                        firstPageText="⇤"
                                        prevPageText="⇠"
                                        lastPageText="⇥"
                                        nextPageText="⇢"
                                        activeLinkClass={classes.ActiveLink}
                                        activePage={currentAccountsPage}
                                        itemsCountPerPage={postsPerAccountsPage}
                                        totalItemsCount={accountsListCount}
                                        pageRangeDisplayed={5}
                                        onChange={(page) => setPage(page, postsPerAccountsPage)}
                                    />
                                </div>
                            )}
                        </div>

                        <AccountEditing
                            data={accountInfo}
                            token={token}
                            onChangeAccountData={onChangeAccountData}
                            projectsList={projects}
                            isDataLoading={loadingAccountInfo}
                        />
                    </Column>
                    <Column width="350" className={filtersClasses.SidebarColumn}>
                        <ResponsiveSidebarFilters
                            title="Фильтры"
                            isOpen={isFiltersOpen}
                            onClose={() => setIsFiltersOpen(false)}
                            onApplyFilters={applyFilters}
                            isApplyDisabled={loadingAccountsList}
                        >
                            <EmptyCard>
                                <DateRange onSelectDate={(range) => setFilterDate(range)} defaultPeriod={'zero'} />
                            </EmptyCard>

                            <AccountsFilters
                                isLoading={loadingAccountsList}
                                onSelectFilter={selectFilter}
                                onApplyFilters={applyFilters}
                            />
                        </ResponsiveSidebarFilters>
                    </Column>
                </Container>
            </Context.Provider>
        </Wrapper>
    );
});

export default Accounts;



// WEBPACK FOOTER //
// ./src/PagesOfAdministrator/Accounts/index.js